import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wrench, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState("loading"); // loading | valid | invalid | success
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      setError("Missing invitation token.");
      return;
    }
    api.get(`/auth/invite/${token}`)
      .then((r) => { setInvite(r.data); setState("valid"); })
      .catch((e) => { setError(formatApiError(e)); setState("invalid"); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/accept-invite", { token, password });
      setState("success");
      // reload auth state so /me returns the new user
      if (refresh) await refresh();
      setTimeout(() => navigate("/", { replace: true }), 1500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-blue-700 flex items-center justify-center text-white">
            <Wrench className="w-5 h-5" />
          </div>
          <div className="font-heading font-extrabold text-2xl text-slate-900">FixTrack</div>
        </div>

        {state === "loading" && (
          <Card className="border-slate-200"><CardContent className="p-8 text-center text-slate-500">
            Checking your invitation…
          </CardContent></Card>
        )}

        {state === "invalid" && (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Invitation invalid</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">{error || "This invitation is invalid, expired, or already used."}</p>
              <Button variant="outline" onClick={() => navigate("/login")}>Go to login</Button>
            </CardContent>
          </Card>
        )}

        {state === "valid" && invite && (
          <Card className="border-slate-200" data-testid="accept-invite-card">
            <CardHeader>
              <CardTitle className="font-heading">Set your password</CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Welcome, <strong>{invite.name}</strong> — you&apos;ve been invited as{" "}
                <strong>{invite.role}</strong>. Set a password to activate your account.
              </p>
              <p className="text-xs text-slate-500 mt-1">Email: {invite.email}</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ai-password">New password *</Label>
                  <Input
                    id="ai-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    data-testid="accept-password"
                  />
                  <p className="text-xs text-slate-500">Minimum 8 characters.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ai-confirm">Confirm password *</Label>
                  <Input
                    id="ai-confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    data-testid="accept-confirm"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-700 hover:bg-blue-800"
                  data-testid="accept-submit"
                >
                  {submitting ? "Activating…" : "Activate account"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {state === "success" && (
          <Card className="border-emerald-200" data-testid="accept-success-card">
            <CardContent className="p-8 text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <div className="font-heading text-xl font-bold text-slate-900">Account activated!</div>
              <p className="text-sm text-slate-600">Redirecting to the dashboard…</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
